import { motion } from "framer-motion";
import {
  Copy,
  Zap,
  Sparkles,
  Search,
  Tag,
  PackageCheck,
  LayoutDashboard,
  Check,
  ArrowRight,
  Coins,
  Gift,
  CheckCircle2,
} from "lucide-react";
import { Badge } from "../ui/badge";
import { FEATURES } from "../../lib/landing-data";

const ICONS = { Copy, Zap, Sparkles, Search, Tag, PackageCheck, LayoutDashboard };

const BADGE_STYLES = {
  Bestseller: "bg-amber-100 text-amber-800",
  New: "bg-purple-100 text-purple-700",
  "Save Money": "bg-emerald-100 text-emerald-700",
};

const ACCESS_META = {
  paid: { label: "ELT Credits", icon: Coins, className: "bg-purple-50 text-purple-700 border-purple-200" },
  free: { label: "Free", icon: Gift, className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  limited: {
    label: "Free basic · Paid full",
    icon: CheckCircle2,
    className: "bg-amber-50 text-amber-800 border-amber-200",
  },
};

export const Features = () => {
  return (
    <section id="features" className="relative py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider">
            Core Features · 7 tools
          </span>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
            Everything you need to sell smarter on{" "}
            <span className="bg-gradient-to-br from-purple-700 to-violet-600 bg-clip-text text-transparent">
              Flipkart & Meesho
            </span>
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg">
            Seven powerful tools in one free Chrome Extension — built specifically for Indian sellers.
          </p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => {
            const Icon = ICONS[f.icon] || Sparkles;
            const access = ACCESS_META[f.access];
            const AccessIcon = access?.icon;
            return (
              <motion.article
                key={f.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                whileHover={{ y: -6 }}
                data-testid={`feature-card-${f.id}`}
                className={`group relative rounded-3xl border border-slate-200 bg-white p-6 sm:p-7 hover:border-purple-300 hover:shadow-xl hover:shadow-purple-600/5 transition-all overflow-hidden ${
                  f.id === "dashboard" ? "lg:col-span-1" : ""
                }`}
              >
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-purple-100/0 group-hover:bg-purple-100/60 blur-2xl transition-colors" />
                <div className="relative">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-600 to-violet-700 text-white flex items-center justify-center shadow-lg shadow-purple-600/25 group-hover:scale-110 transition-transform">
                      <Icon className="w-5.5 h-5.5" strokeWidth={2.2} />
                    </div>
                    {f.badge && (
                      <Badge className={`rounded-full font-semibold ${BADGE_STYLES[f.badge] || "bg-purple-100 text-purple-700"} hover:opacity-90`}>
                        {f.badge}
                      </Badge>
                    )}
                  </div>
                  <h3 className="font-heading text-xl font-extrabold text-slate-900">{f.title}</h3>

                  {access && (
                    <div
                      data-testid={`feature-access-${f.id}`}
                      className={`mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${access.className}`}
                    >
                      {AccessIcon && <AccessIcon className="w-3 h-3" />}
                      {access.label}
                    </div>
                  )}

                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>

                  <ul className="mt-4 space-y-2">
                    {f.points.slice(0, 4).map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 mt-0.5 text-purple-600 shrink-0" strokeWidth={2.5} />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 inline-flex items-center text-sm font-semibold text-purple-700 group-hover:text-purple-900">
                    Learn more
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
